import type { ReactNode } from 'react'

/**
 * Чего приложение показать не может, потому что этого нет в API.
 *
 * Такие места помечаем красным и целым блоком, а не мелкой подписью: иначе
 * экран выглядит законченным, а данных за ним нет, и это всплывает уже у
 * пользователя. Красный здесь — не ошибка пользователя, а незакрытый долг
 * перед ним, и он должен быть виден с одного взгляда.
 *
 * Метка называет само поле и ту ручку, в которой его не хватает, — с этим
 * текстом можно идти к бэкенду, не разбираясь в коде.
 *
 * Живёт в общем пакете, потому что нужна обеим панелям и должна выглядеть в
 * них одинаково: пропущенное поле не становится другим оттого, в каком
 * приложении его не хватает. Список открытых мест — `docs/open-questions.md`.
 */
export interface GapProps {
  /** Чего не хватает: «Вариации товара», «Статус клиента». */
  what: string
  /** Где именно: «GET /catalog/products/{id}» — так его проще найти в контракте. */
  where: string
}

/** Значок метки: рисуем на месте, чтобы пакет не зависел от спрайта приложения. */
function Mark() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        d="M12 3 2 21h20L12 3Zm0 6v6m0 3v.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Метка внутри строки текста: подсвечивает себя, а не всё предложение. */
export function Gap({ what, where }: GapProps) {
  return (
    <span className="gap" title={`${what}: нет в ${where}`}>
      <Mark />
      нет в API
    </span>
  )
}

/**
 * Строка «показатель → значение», в которой значения нет. Красной становится
 * вся строка: пустое место справа само по себе ничего не объясняет.
 */
export function GapRow({ what, where }: GapProps) {
  return (
    <div className="gap-row" title={`${what}: нет в ${where}`}>
      <span>{what}</span>
      <Gap what={what} where={where} />
    </div>
  )
}

export interface GapBlockProps extends GapProps {
  /** Что это значит для работы: почему без этого поля можно жить дальше. */
  children?: ReactNode
}

/** Целый блок, которого нет: вместо пустого экрана — объяснение. */
export function GapPanel({ what, where, children }: GapBlockProps) {
  return (
    <section className="gap-panel">
      <h3>
        {what}
        <Gap what={what} where={where} />
      </h3>
      <div className="gap-panel-text">{children}</div>
      <div className="gap-where">{where}</div>
    </section>
  )
}

/**
 * Пояснение под разделом: чего в ответе нет и почему экран поэтому такой.
 * Целая полоса, а не подпись в скобках, — иначе объяснение читают последним.
 */
export function GapNote({ what, where, children }: GapBlockProps) {
  return (
    <div className="gap-note" title={`${what}: нет в ${where}`}>
      <Gap what={what} where={where} />
      <div>{children}</div>
    </div>
  )
}

/** Заголовок колонки, данных для которой в ответе нет. */
export function GapHead({ what, where, children }: GapBlockProps) {
  return (
    <span className="gap-head" title={`${what}: нет в ${where}`}>
      {children}
      <Gap what={what} where={where} />
    </span>
  )
}
